import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { CURRENCIES, CURRENCY_META, formatAmount, formatUsd, shortAddress } from "@/lib/api";
import { GAMES } from "@/lib/games";
import { usePrices, usdTotal, usdValue } from "@/lib/usePrices";
import { Wallet, ChevronDown, LogOut, Search, User } from "lucide-react";
import { Logo } from "./Logo";
import NotificationBell from "./NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

function GameSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const hits = q.trim() ? GAMES.filter((g) => g.name.toLowerCase().includes(q.trim().toLowerCase())) : GAMES;

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={box} className="relative hidden lg:block w-full max-w-sm">
      <div className="flex items-center gap-2 rounded-xl px-3 h-10 bg-white/[0.04] border border-white/[0.08] focus-within:border-amber-500/50" style={{ transition: "border-color 160ms ease" }}>
        <Search size={15} className="text-zinc-500" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder="Search games"
          className="bg-transparent outline-none text-sm flex-1 placeholder:text-zinc-600" data-testid="game-search-input" />
      </div>
      {open && (
        <div className="absolute top-12 left-0 right-0 panel p-1.5 z-50 fade-up" data-testid="game-search-results">
          {hits.length === 0 ? <div className="px-3 py-2 text-xs text-zinc-500">No games found</div> : hits.map((g) => {
            const Icon = g.icon;
            return (
              <button key={g.slug} onClick={() => { nav(`/games/${g.slug}`); setOpen(false); setQ(""); }} data-testid={`search-hit-${g.slug}`}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 text-left" style={{ transition: "background-color 140ms ease" }}>
                <span className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${g.accent}1f`, color: g.accent }}><Icon size={14} /></span>
                <span className="text-sm font-semibold flex-1">{g.name}</span>
                <span className="text-[10px] uppercase tracking-widest text-zinc-600">Original</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Header({ onConnect }) {
  const { user, logout } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const nav = useNavigate();
  const bal = user?.balances?.[currency] ?? 0;
  const meta = CURRENCY_META[currency];
  const prices = usePrices();
  const total = usdTotal(prices, user?.balances);

  return (
    <header className="sticky top-0 z-30 header-bar">
      <div className="flex items-center gap-4 px-4 md:px-8 h-16">
        <div onClick={() => nav("/")} className="md:hidden cursor-pointer shrink-0" data-testid="mobile-logo"><Logo size="w-9 h-9 text-base" /></div>
        <GameSearch />
        <div className="flex-1" />

        {user ? (
          <div className="flex items-center gap-2">
            <div className="wallet-pill" data-testid="header-balance">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-3 pr-2 h-full hover:bg-white/5 rounded-l-xl" data-testid="currency-switcher" style={{ transition: "background-color 140ms ease" }}>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color, boxShadow: `0 0 10px ${meta.color}88` }} />
                    <span className="flex flex-col items-start leading-none">
                      <span className="font-mono-num text-sm font-bold" data-testid="wallet-balance">{formatAmount(bal, currency)} <span className="text-[10px] text-zinc-500 font-semibold">{currency}</span></span>
                      {prices && <span className="font-mono-num text-[10px] text-zinc-500 mt-0.5 whitespace-nowrap" data-testid="wallet-usd-total">≈ {formatUsd(total)}<span className="hidden sm:inline"> total</span></span>}
                    </span>
                    <ChevronDown size={13} className="text-zinc-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#141416] border-white/10 text-white min-w-[220px]">
                  <DropdownMenuLabel className="text-zinc-500 text-[10px] uppercase tracking-wider flex items-center justify-between">Wallet {prices && <span className="font-mono-num text-white normal-case tracking-normal" data-testid="wallet-usd-total-menu">≈ {formatUsd(total)}</span>}</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {CURRENCIES.map((c) => (
                    <DropdownMenuItem key={c} onClick={() => setCurrency(c)} data-testid={`currency-opt-${c}`} className="focus:bg-white/5 cursor-pointer">
                      <span className="w-2 h-2 rounded-full mr-2" style={{ background: CURRENCY_META[c].color }} />
                      <span className="flex-1 font-semibold">{c}</span>
                      <span className="flex flex-col items-end leading-tight">
                        <span className="font-mono-num text-xs text-zinc-300">{formatAmount(user?.balances?.[c] ?? 0, c)}</span>
                        {prices && <span className="font-mono-num text-[10px] text-zinc-500" data-testid={`currency-usd-${c}`}>{formatUsd(usdValue(prices, c, user?.balances?.[c]))}</span>}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <button onClick={() => nav("/deposit")} className="btn-gold h-full px-4 rounded-r-xl flex items-center gap-2 text-sm font-bold" data-testid="header-deposit-btn">
                <Wallet size={15} /> <span className="hidden sm:inline">Wallet</span>
              </button>
            </div>

            <NotificationBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="avatar-btn" data-testid="user-menu">
                  <span className="font-display font-black text-xs">{(user.username || "U").slice(0, 1).toUpperCase()}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#141416] border-white/10 text-white min-w-[220px]">
                <DropdownMenuLabel className="text-xs">
                  <div className="font-semibold">{user.username}</div>
                  <div className="text-zinc-500 font-mono-num text-[11px]">{user.wallet ? shortAddress(user.wallet) : user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => nav("/dashboard")} className="focus:bg-white/5">Dashboard</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/provably-fair")} className="focus:bg-white/5">Provably Fair</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/vip")} className="focus:bg-white/5" data-testid="menu-vip">VIP Club</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/withdraw")} className="focus:bg-white/5" data-testid="menu-withdraw">Withdraw</DropdownMenuItem>
                {user.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")} className="focus:bg-white/5">Admin Panel</DropdownMenuItem>}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="focus:bg-white/5 text-red-400">
                  <LogOut size={14} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={onConnect} className="btn-ghost rounded-xl px-4 h-10 text-sm hidden sm:flex items-center gap-2" data-testid="signin-btn"><User size={15} /> Sign in</button>
            <button onClick={onConnect} className="btn-gold rounded-xl px-4 h-10 flex items-center gap-2 text-sm font-bold" data-testid="connect-btn"><Wallet size={16} /> Connect</button>
          </div>
        )}
      </div>
    </header>
  );
}
